/**
 * Merchant & Webstore Brand Resolution Utility
 * Provides professional brand display names for popular global/regional stores,
 * and clean domain parsing fallback for unlisted retailers.
 */

const KNOWN_MERCHANTS: Record<string, string> = {
  // Global Marketplaces & Retailers
  'amazon.com': 'Amazon',
  'amazon.sa': 'Amazon SA',
  'amazon.eg': 'Amazon Egypt',
  'amazon.ae': 'Amazon UAE',
  'amazon.co.uk': 'Amazon UK',
  'amazon.de': 'Amazon Germany',
  'amazon.fr': 'Amazon France',
  'amazon.es': 'Amazon Spain',
  'amazon.it': 'Amazon Italy',
  'amazon.ca': 'Amazon Canada',
  'amazon.in': 'Amazon India',
  'amazon.com.tr': 'Amazon Turkey',
  'amazon.com.mx': 'Amazon Mexico',
  'amazon.com.br': 'Amazon Brazil',
  'amazon.nl': 'Amazon Netherlands',
  'amazon.pl': 'Amazon Poland',
  'amazon.se': 'Amazon Sweden',
  'amazon.sg': 'Amazon Singapore',
  'amazon.co.jp': 'Amazon Japan',
  'amazon.com.au': 'Amazon Australia',

  'walmart.com': 'Walmart',
  'walmart.ca': 'Walmart',

  'ebay.com': 'eBay',
  'ebay.co.uk': 'eBay',
  'ebay.de': 'eBay',
  'ebay.fr': 'eBay',
  'ebay.it': 'eBay',
  'ebay.es': 'eBay',
  'ebay.ca': 'eBay',
  'ebay.com.au': 'eBay',

  'aliexpress.com': 'AliExpress',
  'aliexpress.us': 'AliExpress',

  'costco.com': 'Costco',
  'costco.ca': 'Costco',
  'costco.co.uk': 'Costco',

  'bestbuy.com': 'Best Buy',
  'bestbuy.ca': 'Best Buy',

  'target.com': 'Target',
  'newegg.com': 'Newegg',
  'newegg.ca': 'Newegg',
  'bhphotovideo.com': 'B&H Photo Video',
  'microcenter.com': 'Micro Center',
  'homedepot.com': 'The Home Depot',
  'wayfair.com': 'Wayfair',
  'wayfair.co.uk': 'Wayfair',
  'wayfair.ca': 'Wayfair',
  'wayfair.de': 'Wayfair',
  'etsy.com': 'Etsy',
  'ikea.com': 'IKEA',
  'apple.com': 'Apple',

  // Middle East & North Africa (MENA) Popular Stores
  'noon.com': 'Noon',
  'noon.sa': 'Noon',
  'noon.eg': 'Noon',
  'jumia.com.eg': 'Jumia',
  'jumia.com': 'Jumia',
  'jumia.com.ng': 'Jumia',
  'jumia.co.ke': 'Jumia',
  'jumia.ma': 'Jumia',
  'jarir.com': 'Jarir Bookstore',
  'extra.com': 'eXtra',
  'carrefour.com': 'Carrefour',
  'carrefour.sa': 'Carrefour KSA',
  'carrefouregypt.com': 'Carrefour Egypt',
  'carrefouruae.com': 'Carrefour UAE',
  'carrefour.fr': 'Carrefour',
  'carrefour.es': 'Carrefour',
  'sharafdg.com': 'Sharaf DG',
  'luluhypermarket.com': 'LuLu Hypermarket',
  'othaimmarkets.com': 'Al Othaim Markets',
  'panda.com.sa': 'Panda',
  'danube.sa': 'Danube',
  'tamimimarkets.com': 'Tamimi Markets',
  'namshi.com': 'Namshi',
  'btech.com': 'B.TECH',
  '2b.com.eg': '2B Egypt',
  'elarabygroup.com': 'Elaraby Group',
  'radioshack.com.eg': 'RadioShack Egypt',
  'cairocart.com': 'CairoCart',
  'dubizzle.com': 'Dubizzle',
  'haraj.com.sa': 'Haraj',

  // Fashion & Apparel Brands
  'shein.com': 'SHEIN',
  'asos.com': 'ASOS',
  'zara.com': 'Zara',
  'hm.com': 'H&M',
  'nike.com': 'Nike',
  'adidas.com': 'Adidas',
  'sephora.com': 'Sephora',
  'sephora.sa': 'Sephora',
  'sephora.ae': 'Sephora',
  'trendyol.com': 'Trendyol',
};

// Compound public suffixes (ordered by length descending)
const COMPOUND_SUFFIXES = [
  '.com.sa',
  '.com.eg',
  '.com.tr',
  '.co.uk',
  '.com.au',
  '.com.br',
  '.com.mx',
  '.co.za',
  '.co.jp',
  '.co.ke',
  '.com.ng',
  '.com.pk',
  '.com.kw',
  '.com.qa',
  '.com.bh',
  '.com.om',
  '.com.jo',
  '.edu.eg',
  '.gov.sa',
  '.net.sa',
  '.org.sa',
];

// Subdomains commonly prepended to ecommerce domains
const SUBDOMAIN_PREFIXES = [
  'www.',
  'm.',
  'mobile.',
  'shop.',
  'store.',
  'secure.',
  'online.',
  'ar.',
  'en.',
  'sa.',
  'eg.',
  'ae.',
  'us.',
  'uk.',
  'eu.',
  'global.',
];

/**
 * Normalizes raw hostname / domain string by stripping protocols, ports, and trailing slashes.
 */
export function normalizeDomain(rawDomain: string): string {
  if (!rawDomain) return '';
  let cleaned = rawDomain.trim().toLowerCase();

  // Strip protocol
  cleaned = cleaned.replace(/^[a-zA-Z]+:\/\//, '');

  // Strip port and path/queries
  const pathPart = cleaned.split('/')[0] || '';
  const portPart = pathPart.split(':')[0] || '';
  cleaned = portPart.split('?')[0] || '';

  return cleaned;
}

/**
 * Extracts a clean, human-readable brand name from an unlisted domain.
 * e.g., "shop.gadget-haven.com.eg" -> "Gadget Haven"
 * e.g., "m.boutique-chic.co.uk" -> "Boutique Chic"
 */
export function extractBrandFromDomain(domain: string): string {
  let cleaned = normalizeDomain(domain);
  if (!cleaned) return 'Store';

  // Strip known subdomains repeatedly
  let matchedSub = true;
  while (matchedSub) {
    matchedSub = false;
    for (const prefix of SUBDOMAIN_PREFIXES) {
      if (cleaned.startsWith(prefix)) {
        cleaned = cleaned.slice(prefix.length);
        matchedSub = true;
        break;
      }
    }
  }

  // Strip known compound suffixes
  for (const suffix of COMPOUND_SUFFIXES) {
    if (cleaned.endsWith(suffix)) {
      cleaned = cleaned.slice(0, -suffix.length);
      break;
    }
  }

  // Strip remaining single-segment TLDs (.com, .sa, .org, etc.)
  cleaned = cleaned.replace(/\.[a-z0-9]{2,}$/i, '');

  if (!cleaned) return 'Store';

  // Convert hyphens and underscores to spaces and capitalize each word
  return cleaned
    .replace(/[-_]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Resolves the professional display name for any webstore domain.
 * First checks curated retailer map; falls back to smart domain brand extraction.
 */
export function getMerchantName(rawDomain: string | null | undefined): string {
  if (!rawDomain) return 'Store';

  const normalized = normalizeDomain(rawDomain);
  if (!normalized) return 'Store';

  // 1. Direct exact lookup in curated dictionary
  if (KNOWN_MERCHANTS[normalized]) {
    return KNOWN_MERCHANTS[normalized];
  }

  // 2. Strip subdomains and check if root domain is in curated dictionary
  // e.g. "www.amazon.sa" -> "amazon.sa" -> "Amazon SA"
  for (const prefix of SUBDOMAIN_PREFIXES) {
    if (normalized.startsWith(prefix)) {
      const stripped = normalized.slice(prefix.length);
      if (KNOWN_MERCHANTS[stripped]) {
        return KNOWN_MERCHANTS[stripped];
      }
    }
  }

  // 3. Fallback to clean domain brand parser
  return extractBrandFromDomain(normalized);
}
